import {
  deleteInternalCategory,
  downloadInternalCategoriesDlcTrackingXlsx,
  useInternalCategories,
} from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Download, Eye, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { getAxiosErrorMessage } from '~/lib/utils'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { useRights } from '../../../utils/hooks'
import { InternalCategoryDialog } from './InternalCategoryDialog'

export const InternalCategoriesPanel = () => {
  const isUserAdmin = useRights(['ADMIN'])

  const [search, setSearch] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<number[]>([])
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const { data: internalCategories = [], refetch, isLoading } = useInternalCategories()

  const filteredCategories = useMemo(
    () => internalCategories.filter((category) => includesLowerCase(category.name, search)),
    [internalCategories, search],
  )

  const deleteCategoryMut = useMutation({ mutationFn: deleteInternalCategory })

  const exportDlcMut = useMutation({
    mutationFn: downloadInternalCategoriesDlcTrackingXlsx,
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = url
      const today = new Date().toISOString().slice(0, 10)
      link.download = `suivi-dlc-${today}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    onError: (error) => {
      toast.error(getAxiosErrorMessage(error, "Erreur lors de l'export DLC"))
    },
  })

  const handleDeleteCategory = async () => {
    try {
      await deleteCategoryMut.mutateAsync(selectedCategories)
      setSelectedCategories([])
      refetch()
    } catch (error) {
      let message = 'Une erreur inconnue est survenue lors de la suppression de la catégorie'
      if (
        error instanceof AxiosError &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data
      ) {
        message = error.response.data.message as string
      }
      toast.error(message)
    } finally {
      setDeleteConfirmOpen(false)
    }
  }

  const toggleSelectAll = () => {
    if (selectedCategories.length === filteredCategories.length) {
      setSelectedCategories([])
    } else {
      setSelectedCategories(filteredCategories.map((c) => c.id))
    }
  }

  const toggleSelectCategory = (id: number) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    )
  }

  const deleteDisabled = selectedCategories.length === 0 || deleteCategoryMut.isPending
  const modifyDisabled = selectedCategories.length !== 1 || deleteCategoryMut.isPending
  const allSelected =
    filteredCategories.length > 0 && selectedCategories.length === filteredCategories.length

  const tableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={3} className="px-4 py-8 text-center text-sm text-muted-foreground">
            Chargement...
          </td>
        </tr>
      )
    }
    if (filteredCategories.length === 0) {
      return (
        <tr>
          <td colSpan={3} className="px-4 py-8 text-center text-sm text-muted-foreground">
            Aucune catégorie trouvée
          </td>
        </tr>
      )
    }
    return filteredCategories.map((category) => {
      const isSelected = selectedCategories.includes(category.id)
      return (
        <tr
          key={category.id}
          className={`border-b border-border/50 cursor-pointer transition-colors hover:bg-muted/30 ${isSelected ? 'bg-primary/5' : ''}`}
          onClick={() => toggleSelectCategory(category.id)}
        >
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelectCategory(category.id)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded accent-primary"
            />
          </td>
          <td className="px-4 py-3 text-sm">{category.name}</td>
        </tr>
      )
    })
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center border border-border rounded-lg px-3 py-2 w-64 bg-background">
          <Search size={16} className="text-muted-foreground mr-2 shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Recherche"
            className="flex-1 text-sm outline-none bg-transparent"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => exportDlcMut.mutate()}
            disabled={exportDlcMut.isPending}
            title="Télécharge le suivi DLC (xlsx) pour la semaine passée, restreint aux catégories avec suivi DLC activé"
          >
            {exportDlcMut.isPending ? <Loader2 className="animate-spin" /> : <Download />}
            Export suivi DLC (S-1)
          </Button>
          {isUserAdmin && (
            <Button
              variant="destructive"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={deleteDisabled}
            >
              <Trash2 />
              Supprimer
            </Button>
          )}
          <Button
            variant="outline"
            disabled={modifyDisabled}
            onClick={() => setEditDialogOpen(true)}
          >
            {isUserAdmin ? (
              <>
                <Pencil />
                Modifier
              </>
            ) : (
              <>
                <Eye />
                Voir
              </>
            )}
          </Button>
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus />
            Ajouter une catégorie interne
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="mx-4 mb-4 bg-background rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded accent-primary"
                />
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Nom
              </th>
            </tr>
          </thead>
          <tbody>{tableBody()}</tbody>
        </table>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Êtes-vous sûr de vouloir supprimer{' '}
            {selectedCategories.length === 1 ? 'cette catégorie' : 'ces catégories'} ?
          </p>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
            <Button
              variant="destructive"
              onClick={handleDeleteCategory}
              disabled={deleteCategoryMut.isPending}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <InternalCategoryDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        internalCategoryId={editDialogOpen ? selectedCategories[0] : undefined}
        onSuccess={() => {
          refetch()
          setSelectedCategories([])
        }}
      />

      {/* Add dialog */}
      <InternalCategoryDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={refetch}
      />
    </>
  )
}
